import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  isPushSupported, isStandalone, isIOS, permissionState,
  subscribePush, unsubscribePush, getPushStatus, sendTestPush,
} from "@/lib/push";
import { Bell, Loader2, Send, Smartphone, Info } from "lucide-react";

export function NotificationsSettings() {
  const { isAdmin } = useAuth();
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [perm, setPerm] = useState("default");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    const init = async () => {
      const sup = isPushSupported();
      if (!active) return;
      setSupported(sup);
      setPerm(permissionState());
      if (sup) {
        try {
          const s = await getPushStatus();
          if (active) setSubscribed(!!s.subscribed);
        } catch (e) { /* leave default */ }
      }
      if (active) setReady(true);
    };
    init();
    return () => { active = false; };
  }, []);

  const iosNeedsInstall = isIOS() && !isStandalone();
  const blocked = perm === "denied";
  const canToggle = supported && !iosNeedsInstall && (!blocked || subscribed);

  let hint = null;
  if (!supported) hint = "This browser doesn't support push notifications. Try Chrome, Edge, or Safari 16.4+.";
  else if (iosNeedsInstall) hint = "On iPhone or iPad, add Friday Conversations to your Home Screen first (Share → Add to Home Screen), then turn this on.";
  else if (blocked) hint = "Notifications are blocked in your browser settings. Allow them for this site, then turn this on.";

  const handleToggle = async (v) => {
    setBusy(true);
    try {
      if (v) {
        await subscribePush();
        setSubscribed(true);
        toast.success("Notifications enabled on this device");
      } else {
        await unsubscribePush();
        setSubscribed(false);
        toast.success("Notifications turned off on this device");
      }
      setPerm(permissionState());
    } catch (e) {
      const code = e?.code;
      if (code === "denied") toast.error("Permission blocked. Allow notifications in your browser settings.");
      else if (code === "dismissed") toast("Permission was dismissed — tap again to enable.");
      else if (code === "unsupported") toast.error("This browser doesn't support push notifications.");
      else if (code === "disabled") toast.error("Push notifications aren't available right now.");
      else toast.error("Could not update notifications. Please try again.");
      try { const s = await getPushStatus(); setSubscribed(!!s.subscribed); } catch (_) { /* noop */ }
      setPerm(permissionState());
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Test notification sent — check your device");
    } catch (e) {
      toast.error(
        e?.response?.status === 404
          ? "No active device found. Turn notifications on first."
          : "Could not send a test notification."
      );
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-secondary/60 p-4" data-testid="notifications-settings">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-foreground">
            <Bell className="h-4 w-4 text-primary" /> Push notifications
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Get a heads-up when we go live, reminders before events, and an alert when you're admitted to the studio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Switch
            checked={subscribed}
            disabled={!ready || busy || !canToggle}
            onCheckedChange={handleToggle}
            data-testid="notifications-toggle"
            aria-label="Enable push notifications"
          />
        </div>
      </div>

      {hint && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-background/60 p-2.5 text-xs text-muted-foreground" data-testid="notifications-hint">
          {iosNeedsInstall ? <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> : <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />}
          <span>{hint}</span>
        </div>
      )}

      {isAdmin && subscribed && (
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="notifications-test-btn">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send test notification
          </Button>
        </div>
      )}
    </div>
  );
}

export default NotificationsSettings;
